import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, Heart, Droplet, Scale, TrendingUp, Apple, Dumbbell, Brain, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";

const chartData = [
  { date: "Jan", healthScore: 75 },
  { date: "Feb", healthScore: 78 },
  { date: "Mar", healthScore: 77 },
  { date: "Apr", healthScore: 80 },
  { date: "May", healthScore: 82 },
  { date: "Jun", healthScore: 85 },
];

const chartConfig = {
  healthScore: {
    label: "Health Score",
    color: "hsl(231, 50%, 51%)",
  },
};

const Dashboard = () => {
  const [userData, setUserData] = useState<any>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch("/hms/php/get_user_data.php", {
          method: "GET",
          credentials: "include", // ✅ ensures cookies/sessions are sent
        });
  
        const data = await response.json();
        console.log("Dashboard data:", data); // 👈 check in browser console
  
        if (data.status === "success") {
          setUserData(data.user);
        } else {
          console.error("Error:", data.message);
          setError(data.message);
        }
      } catch (err) {
        console.error("Network error:", err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchDashboardData();
  }, []);
  

  const quickActions = [
    { title: "New Health Assessment", description: "Get an updated risk analysis", icon: Brain, href: "/new-assessment" },
    { title: "Daily Nutrition Plan", description: "Explore today's meal recommendations", icon: Apple, href: "/nutrition" },
    { title: "Your Fitness Regimen", description: "Review and log your workouts", icon: Dumbbell, href: "/fitness" },
    { title: "Progress Overview", description: "Visualize your health journey", icon: TrendingUp, href: "/progress" },
  ];

  if (loading) return <p className="p-8 text-center text-lg">Loading dashboard...</p>;
  if (error) return <p className="p-8 text-center text-red-600">{error}</p>;
  if (!userData) return <p className="p-8 text-center">No data available.</p>;

  // Personalized health metrics (replace static ones)
  const healthMetrics = [
    {
      label: "BMI",
      value: userData.bmi,
      unit: "",
      icon: Scale,
      status: "Healthy",
      trend: "Updated from latest profile data",
      color: "text-primary",
    },
    {
      label: "Calorie Target",
      value: userData.calorie_target,
      unit: "",
      icon: Apple,
      status: "Custom",
      trend: `${userData.activity_level} activity`,
      color: "text-green-600",
    },
    {
      label: "Weight",
      value: userData.weight,
      unit: "kg",
      icon: TrendingUp,
      status: "Current",
      trend: "Based on latest record",
      color: "text-primary",
    },
    {
      label: "Height",
      value: userData.height,
      unit: "cm",
      icon: Activity,
      status: "Fixed",
      trend: "Profile data",
      color: "text-muted-foreground",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold mb-2">
          Welcome to Your Health Dashboard!
          </h2>
          <p className="text-muted-foreground">
            {`You're doing great, ${userData.fullname}! Here's your personalized health summary.`}
          </p>
        </div>

        {/* Health Metrics Grid */}
        <div className="mb-8 animate-slide-up">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {healthMetrics.map((metric, index) => (
              <Card key={index} className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <metric.icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">{metric.label}</p>
                    <div className="flex items-baseline gap-1">
                      <p className="text-3xl font-bold">{metric.value}</p>
                      {metric.unit && <span className="text-sm text-muted-foreground">{metric.unit}</span>}
                    </div>
                    <p className="text-xs text-green-600">{metric.trend}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Health Score Chart */}
        <div className="mb-8 animate-slide-up">
          <Card>
            <CardHeader>
              <CardTitle>Health Score Trend</CardTitle>
              <CardDescription>An overview of your health score over the last 6 months.</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[200px] w-full">
                <LineChart data={chartData}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Line dataKey="healthScore" type="monotone" stroke="var(--color-healthScore)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8 animate-slide-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Your Health Toolkit</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {quickActions.map((action, index) => (
              <Link key={index} to={action.href}>
                <Card className="border shadow-sm hover:shadow-md hover:border-primary/50 transition-all h-full group cursor-pointer">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="p-3 rounded-xl bg-primary/10 w-fit group-hover:bg-primary/20 transition-colors">
                        <action.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">{action.title}</h3>
                        <p className="text-sm text-muted-foreground">{action.description}</p>
                      </div>
                      <div className="flex items-center text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Explore Now <ArrowRight className="ml-1 h-4 w-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Personalized Tip */}
        <div className="animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                Personalized Health Insight
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/90">
                {userData.activity_level === "high"
                  ? "Great job staying active! Keep your protein intake high to support recovery."
                  : userData.activity_level === "moderate"
                  ? "You're doing well! Try adding light evening walks to boost your metabolism."
                  : "Let's get you moving! Even a 15-minute daily walk can make a big difference."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
